import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ExitIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'newsletter' | 'contact' | 'quick-download';
}

const PopupContent: React.FC<{ type: ExitIntentPopupProps['type'], onClose: () => void }> = ({ type, onClose }) => {
  const navigate = useNavigate();

  switch (type) {
    case 'newsletter':
      return (
        <>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Stay Updated with Healthcare Insights
          </h3>
          <p className="text-gray-600 mb-6">
            Join our newsletter to receive weekly tips on patient education and practice growth.
          </p>
          <form className="space-y-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Subscribe Now
            </button>
          </form>
        </>
      );

    case 'contact':
      return (
        <>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Don't Leave Yet!
          </h3>
          <p className="text-gray-600 mb-6">
            Complete your consultation request and get a free digital assessment worth $500.
          </p>
          <button
            className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
              onClose();
            }}
          >
            Complete Consultation Request
          </button>
        </>
      );

    case 'quick-download':
      return (
        <>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Free Guide: Improve Patient Education
          </h3>
          <p className="text-gray-600 mb-6">
            Download our comprehensive guide on enhancing patient education and engagement.
            No registration required!
          </p>
          <button
            onClick={() => {
              navigate('/resources/patient-education-guide.pdf');
              onClose();
            }}
            className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Download Now
          </button>
        </>
      );
  }
};

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({ isOpen, onClose, type }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      const timeout = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      } transition-opacity duration-300`}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        className={`relative bg-white rounded-lg shadow-xl w-full max-w-md p-8 transform ${
          isOpen ? 'scale-100' : 'scale-95'
        } transition-transform duration-300`}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
        <PopupContent type={type} onClose={onClose} />
      </div>
    </div>
  );
};

export default ExitIntentPopup;